'use client';

import Image from '@/components/Image';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type ReactNode, useState } from 'react';
import type { CapsuleArchiveData, CapsuleArchiveItem } from '../../../types';
import { buildCapsuleArchiveAdJumpProperties, resolveCapsuleAssetUrl } from '../utils';

interface CapsuleArchiveProps {
  data: CapsuleArchiveData;
  isWap?: boolean;
  onItemClick?: (item: CapsuleArchiveItem, index: number) => void;
  onItemExposure?: (item: CapsuleArchiveItem, index: number) => void;
}

interface CapsuleArchiveExposureCardProps {
  children: ReactNode;
  className: string;
  exposureKey: string;
  index: number;
  item: CapsuleArchiveItem;
  onClick?: CapsuleArchiveProps['onItemClick'];
  onExposure?: CapsuleArchiveProps['onItemExposure'];
}

function CapsuleArchiveExposureCard({
  children,
  className,
  exposureKey,
  index,
  item,
  onClick,
  onExposure,
}: CapsuleArchiveExposureCardProps) {
  const router = useRouter();
  const { ref } = useValidExposure({
    callback: () => onExposure?.(item, index),
    ts: exposureKey,
  });
  const jumpProperties = buildCapsuleArchiveAdJumpProperties(item);
  const content = (
    <article ref={ref} className="h-full w-full">
      {children}
    </article>
  );

  if (jumpProperties.pits_jump_content) {
    const href = jumpProperties.pits_jump_content;

    return (
      <Link
        href={href}
        className={className}
        data-capsule-archive-card
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClick?.(item, index);
          router.push(href);
        }}
        prefetch={false}
      >
        {content}
      </Link>
    );
  }

  return (
    <article ref={ref} className={className} data-capsule-archive-card>
      {children}
    </article>
  );
}

const ARCHIVE_PAGE_SIZE = 4;
const WAP_ARCHIVE_VISIBLE_COUNT = 2;

export default function CapsuleArchive({
  data,
  isWap = false,
  onItemClick,
  onItemExposure,
}: CapsuleArchiveProps) {
  const tCapsule = useTranslations('activity.campaigns.capsule');
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const archiveItems = (data.items || []).flatMap((item, index) => {
    const image = isWap
      ? resolveCapsuleAssetUrl(item.cover?.wap) || resolveCapsuleAssetUrl(item.cover?.pc)
      : resolveCapsuleAssetUrl(item.cover?.pc);
    if (!image) return [];

    return [
      {
        image,
        index,
        item,
        key: `${image}-${item.url_prefix}-${item.url}-${index}`,
      },
    ];
  });

  if (!archiveItems.length) return null;

  if (isWap) {
    const canSwipe = archiveItems.length > WAP_ARCHIVE_VISIBLE_COUNT;
    const renderArchiveCard = (archive: (typeof archiveItems)[number]) => (
      <CapsuleArchiveExposureCard
        key={archive.key}
        className="relative aspect-[172/240] w-[calc((100vw_-_31px)/2)] max-w-[172px] shrink-0 snap-start snap-always overflow-hidden"
        exposureKey={archive.key}
        index={archive.index}
        item={archive.item}
        onClick={onItemClick}
        onExposure={onItemExposure}
      >
        <Image
          src={archive.image}
          alt={`${tCapsule('previousIssues')} ${archive.index + 1}`}
          fill
          sizes="(max-width: 374px) calc((100vw - 31px) / 2), 172px"
          className="object-cover object-center"
        />
      </CapsuleArchiveExposureCard>
    );

    return (
      <section
        aria-labelledby={data.title ? 'capsule-archive-title-wap' : undefined}
        className="w-full bg-white pb-[16px] pt-[40px]"
        data-capsule-archive
        data-capsule-archive-wap
        data-marketing-campaign-id={data.marketing_campaign_id}
      >
        <div className="flex flex-col items-center gap-[12px]">
          {data.title || data.sub_title ? (
            <header className="flex flex-col items-center gap-[8px] px-[12px] text-center text-primary-text">
              {data.title ? (
                <h2
                  id="capsule-archive-title-wap"
                  className="font-be text-[24px] font-extrabold leading-normal text-[#111827]"
                >
                  {data.title}
                </h2>
              ) : null}
              {data.sub_title ? (
                <p className="max-w-[351px] font-br text-[12px] leading-normal">{data.sub_title}</p>
              ) : null}
            </header>
          ) : null}

          {canSwipe ? (
            <div
              className="scrollbar-hide w-full snap-x snap-mandatory scroll-px-[12px] overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary-purple"
              aria-labelledby={data.title ? 'capsule-archive-title-wap' : undefined}
              role={data.title ? 'region' : undefined}
              tabIndex={0}
              data-capsule-archive-scroll
            >
              <div className="flex w-max gap-[4px] pl-[12px] pr-[15px]" data-capsule-archive-list>
                {archiveItems.map(renderArchiveCard)}
              </div>
            </div>
          ) : (
            <div
              className="flex w-full justify-center gap-[4px] px-[12px]"
              data-capsule-archive-list
              data-page-index="0"
            >
              {archiveItems.map(renderArchiveCard)}
            </div>
          )}
        </div>
      </section>
    );
  }

  const maxStartIndex = Math.max(archiveItems.length - ARCHIVE_PAGE_SIZE, 0);
  const activeStartIndex = Math.min(currentStartIndex, maxStartIndex);
  const canMovePrevious = activeStartIndex > 0;
  const canMoveNext = activeStartIndex < maxStartIndex;
  const showArrowButtons = archiveItems.length > ARCHIVE_PAGE_SIZE;

  const moveArchive = (direction: -1 | 1) => {
    setCurrentStartIndex((currentIndex) => {
      const safeCurrentIndex = Math.min(currentIndex, maxStartIndex);
      return Math.min(Math.max(safeCurrentIndex + direction, 0), maxStartIndex);
    });
  };

  return (
    <section
      aria-labelledby={data.title ? 'capsule-archive-title' : undefined}
      className="mx-auto w-[calc(100%_-_96px)] max-w-[1824px] pb-[40px] pt-0 min-[1025px]:pb-[60px] min-[1281px]:pb-[70px] min-[1441px]:pb-[80px]"
      data-capsule-archive
      data-marketing-campaign-id={data.marketing_campaign_id}
    >
      <div className="flex flex-col items-center gap-[32px]">
        {data.title || data.sub_title ? (
          <header className="flex flex-col items-center gap-[12px] text-center text-primary-text">
            {data.title ? (
              <h2
                id="capsule-archive-title"
                className="font-be text-[clamp(32px,2.5vw,48px)] font-extrabold leading-normal"
              >
                {data.title}
              </h2>
            ) : null}
            {data.sub_title ? (
              <p className="font-br text-[clamp(16px,1.25vw,24px)] leading-normal">
                {data.sub_title}
              </p>
            ) : null}
          </header>
        ) : null}

        <div className="relative w-full overflow-hidden">
          <div
            id="capsule-archive-pages"
            className={`flex gap-[24px] transition-transform duration-500 ease-out motion-reduce:transition-none ${archiveItems.length <= ARCHIVE_PAGE_SIZE ? 'justify-center' : ''}`}
            style={{
              transform: `translate3d(calc(-${activeStartIndex * 25}% - ${activeStartIndex * 6}px), 0, 0)`,
            }}
            data-active-start-index={activeStartIndex}
            data-capsule-archive-list
            data-capsule-archive-track
          >
            {archiveItems.map((archive) => (
              <CapsuleArchiveExposureCard
                key={archive.key}
                className="relative aspect-[438/609] w-[calc((100%_-_72px)/4)] shrink-0 overflow-hidden"
                exposureKey={archive.key}
                index={archive.index}
                item={archive.item}
                onClick={onItemClick}
                onExposure={onItemExposure}
              >
                <Image
                  src={archive.image}
                  alt={`${tCapsule('previousIssues')} ${archive.index + 1}`}
                  fill
                  sizes="25vw"
                  className="object-cover object-center"
                />
              </CapsuleArchiveExposureCard>
            ))}
          </div>

          {showArrowButtons ? (
            <>
              <button
                type="button"
                aria-label={tCapsule('previousIssues')}
                aria-controls="capsule-archive-pages"
                disabled={!canMovePrevious}
                className="absolute left-0 top-1/2 z-10 flex h-[54px] w-[32px] -translate-y-1/2 items-center justify-center bg-white/50 disabled:cursor-default"
                onClick={() => moveArchive(-1)}
                data-capsule-archive-previous
              >
                <span
                  aria-hidden="true"
                  className="size-[24px] bg-[url('/zeelool-next/activity/capsule/left.svg')] bg-contain bg-center bg-no-repeat"
                />
              </button>
              <button
                type="button"
                aria-label={tCapsule('nextIssues')}
                aria-controls="capsule-archive-pages"
                disabled={!canMoveNext}
                className="absolute right-0 top-1/2 z-10 flex h-[54px] w-[32px] -translate-y-1/2 items-center justify-center bg-white/50 disabled:cursor-default"
                onClick={() => moveArchive(1)}
                data-capsule-archive-next
              >
                <span
                  aria-hidden="true"
                  className="size-[24px] bg-[url('/zeelool-next/activity/capsule/right.svg')] bg-contain bg-center bg-no-repeat"
                />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}
